import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { InboxEventType, InboxPreferencesPutBody } from "@evolua/api-client";
import { getApiClient } from "../auth/http.js";
import { useSession } from "../session/SessionContext.js";
import {
  getDemoInboxPreferences,
  getDemoInboxUnreadCount,
  listDemoInbox,
  listDemoPushDeliveries,
  markAllDemoInboxRead,
  markDemoInboxRead,
  putDemoInboxPreferences,
} from "./demo-inbox.js";
import { queryKeys } from "./query-keys.js";

function invalidateInbox(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: queryKeys.me.inboxAll() });
}

export function useMeInbox(
  filters: {
    unreadOnly?: boolean;
    type?: InboxEventType;
  } = {},
) {
  const { isDemoMode } = useSession();
  return useQuery({
    queryKey: queryKeys.me.inbox(filters),
    queryFn: () =>
      isDemoMode
        ? Promise.resolve(listDemoInbox(filters))
        : getApiClient().listMeNotifications(filters),
  });
}

export function useMeInboxUnreadCount(enabled = true) {
  const { isDemoMode } = useSession();
  return useQuery({
    queryKey: queryKeys.me.inboxUnread(),
    queryFn: () =>
      isDemoMode
        ? Promise.resolve(getDemoInboxUnreadCount())
        : getApiClient().getMeNotificationsUnreadCount(),
    enabled,
    refetchInterval: 60_000,
  });
}

export function useMarkMeNotificationRead() {
  const { isDemoMode } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      isDemoMode
        ? Promise.resolve(markDemoInboxRead(id))
        : getApiClient().markMeNotificationRead(id),
    onSuccess: () => invalidateInbox(queryClient),
  });
}

export function useMarkAllMeNotificationsRead() {
  const { isDemoMode } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      isDemoMode
        ? Promise.resolve(markAllDemoInboxRead())
        : getApiClient().markAllMeNotificationsRead(),
    onSuccess: () => invalidateInbox(queryClient),
  });
}

export function useMeNotificationPreferences() {
  const { isDemoMode } = useSession();
  return useQuery({
    queryKey: queryKeys.me.inboxPreferences(),
    queryFn: () =>
      isDemoMode
        ? Promise.resolve(getDemoInboxPreferences())
        : getApiClient().getMeNotificationPreferences(),
  });
}

export function usePutMeNotificationPreferences() {
  const { isDemoMode } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: InboxPreferencesPutBody) =>
      isDemoMode
        ? Promise.resolve(putDemoInboxPreferences(body))
        : getApiClient().putMeNotificationPreferences(body),
    onSuccess: () => invalidateInbox(queryClient),
  });
}

export function useMePushDeliveries() {
  const { isDemoMode } = useSession();
  return useQuery({
    queryKey: queryKeys.me.inboxPush(),
    queryFn: () =>
      isDemoMode
        ? Promise.resolve(listDemoPushDeliveries())
        : getApiClient().listMePushDeliveries(),
  });
}
